/**
 * TagQuickAddDialog — admin cria tag inline no card (FEAT-V2-031).
 *
 * Minimal: nome + cor (icone/descricao opcionais). Workflow herda do contexto
 * (a tag fica restrita ao workflow do card que está sendo editado).
 * Pra criar tags globais, admin usa TagsCatalogPage.
 */

import { useState } from 'react';

import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  Button,
  Input,
  Label,
  Alert,
} from '@/components/ui';
import { TagColorPicker } from './TagColorPicker';
// BUG-V2-054: emoji picker visual (substitui input text livre).
import { TagIconPicker } from './TagIconPicker';
import { TagBadge } from './TagBadge';
import { useTagsCatalogActions, type Tag, type CreateTagPayload } from '@/lib/tags-store';

interface TagQuickAddDialogProps {
  workflowSlug: string | null;
  onClose: () => void;
  onCreated: (tag: Tag) => void;
}

const DEFAULT_COLOR = '#94a3b8'; // slate-400

export function TagQuickAddDialog({
  workflowSlug,
  onClose,
  onCreated,
}: TagQuickAddDialogProps) {
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState(DEFAULT_COLOR);
  const [icone, setIcone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { create } = useTagsCatalogActions();

  async function handleSubmit() {
    const trimmed = nome.trim();
    if (trimmed.length < 1) {
      setError('Nome obrigatório');
      return;
    }
    if (trimmed.length > 30) {
      setError('Nome até 30 caracteres');
      return;
    }
    setError(null);
    try {
      const payload: CreateTagPayload = {
        nome: trimmed,
        cor,
        icone: icone.trim() || null,
        // Quick-add herda escopo do workflow do card. Pra global, admin
        // usa TagsCatalogPage. Decisão pra evitar criar global por acidente.
        workflowsPermitidos: workflowSlug ? [workflowSlug] : null,
      };
      const tag = await create.mutateAsync(payload);
      onCreated(tag);
    } catch (err) {
      const e = err as { response?: { error?: string } };
      if (e?.response?.error === 'tag-name-exists') {
        setError(`Já existe uma tag com nome "${trimmed}"`);
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao criar tag');
      }
    }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>Nova tag</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          <div>
            <Label htmlFor="tag-quick-nome">Nome</Label>
            <Input
              id="tag-quick-nome"
              data-testid="tag-quick-add-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              autoFocus
              maxLength={30}
              placeholder="ex: VIP, Urgente, Reincidente…"
            />
          </div>
          <div>
            <Label>Cor</Label>
            <TagColorPicker value={cor} onChange={setCor} />
          </div>
          <div>
            <Label>Ícone (opcional)</Label>
            {/* BUG-V2-054: picker visual em vez de input text livre.
                Compat retroativa: TagIconPicker exibe emojis fora da
                lista curada como-is (tag antiga continua renderizando). */}
            <TagIconPicker value={icone} onChange={setIcone} />
          </div>
          {nome.trim() && (
            <div>
              <Label>Preview</Label>
              <div className="mt-1">
                <TagBadge
                  tag={{
                    id: 'preview',
                    nome: nome.trim(),
                    cor,
                    icone: icone.trim() || null,
                    ativo: true,
                  }}
                  size="md"
                />
              </div>
            </div>
          )}
          {error && (
            <Alert variant="danger" rail className="text-xs">
              {error}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={create.isPending || !nome.trim()}
          data-testid="tag-quick-add-submit"
        >
          {create.isPending ? 'Criando…' : 'Criar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
